import { useState, useEffect, useCallback } from 'react';
import { MapPin, Tv } from 'lucide-react';
import { useSeo } from '../components/custom/Seo';
import { fetchScores, etDay, formatGameTime } from '../lib/scoresApi';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'commanders', label: 'Commanders' },
  { key: 'nationals', label: 'Nationals' },
  { key: 'capitals', label: 'Capitals' },
  { key: 'wizards', label: 'Wizards' },
  { key: 'mystics', label: 'Mystics' },
  { key: 'dcunited', label: 'DC United' },
];

const StatusBadge = ({ game }) => {
  if (game.status === 'LIVE') {
    return (
      <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-white bg-[#BF0A30] px-2 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        Live{game.status_detail ? ` · ${game.status_detail}` : ''}
      </span>
    );
  }
  if (game.status === 'FINAL') {
    return <span className="text-xs font-bold uppercase tracking-wide text-slate-900">{game.status_detail || 'Final'}</span>;
  }
  if (game.status === 'POSTPONED' || game.status === 'DELAYED') {
    return <span className="text-xs font-bold uppercase tracking-wide text-amber-700">{game.status}</span>;
  }
  return <span className="text-xs font-medium text-slate-500">{formatGameTime(game.date)}</span>;
};

const GameTeamRow = ({ side, opponentScore, isFinal }) => {
  const won = isFinal && side.score != null && opponentScore != null && Number(side.score) > Number(opponentScore);
  const emphasized = !isFinal || won;
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        {side.logo && (
          <img src={side.logo} alt={side.name || side.abbr} width="24" height="24" className="w-6 h-6 object-contain shrink-0" loading="lazy" />
        )}
        <span className={`text-sm truncate ${emphasized ? 'font-bold text-slate-900' : 'font-medium text-slate-500'}`}>
          {side.name}
        </span>
        {side.record && <span className="text-xs text-slate-400 shrink-0">({side.record})</span>}
      </div>
      {side.score != null && (
        <span className={`text-xl tabular-nums ${emphasized ? 'font-bold text-slate-900' : 'font-medium text-slate-400'}`}>
          {side.score}
        </span>
      )}
    </div>
  );
};

// Structured so a future Game Center page can wrap this card in a Link via `to`
export const GameCard = ({ game }) => {
  const isFinal = game.status === 'FINAL';
  const details = [game.venue, game.broadcast].filter(Boolean);
  return (
    <div
      data-testid={`game-card-${game.id}`}
      className={`bg-white border p-4 ${game.status === 'LIVE' ? 'border-[#BF0A30]' : 'border-slate-200'}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">{game.league}</span>
        <StatusBadge game={game} />
      </div>
      <div className="space-y-2.5">
        <GameTeamRow side={game.away} opponentScore={game.home.score} isFinal={isFinal} />
        <GameTeamRow side={game.home} opponentScore={game.away.score} isFinal={isFinal} />
      </div>
      {details.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-slate-100">
          {game.venue && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <MapPin className="w-3 h-3" />
              {game.venue}
            </span>
          )}
          {game.broadcast && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Tv className="w-3 h-3" />
              {game.broadcast}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, accent, games, testid }) => {
  if (games.length === 0) return null;
  return (
    <section data-testid={testid} className="mb-10">
      <h2 className="font-heading text-lg md:text-lg text-slate-900 uppercase tracking-wide flex items-center gap-3 border-b border-slate-200 pb-3 mb-5">
        <span className="inline-block w-1 h-6" style={{ backgroundColor: accent }} />
        {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {games.map((g) => (
          <GameCard key={`${g.team_key}-${g.id}`} game={g} />
        ))}
      </div>
    </section>
  );
};

export default function Scores() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');

  useSeo({
    title: 'DC Scores & Schedule | DC Sports Report',
    description:
      'Live scores, schedules and results for the Washington Commanders, Nationals, Capitals, Wizards, Mystics and DC United.',
    path: '/scores',
  });

  const load = useCallback(async () => {
    setData(await fetchScores());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const live = data && (data.games || []).some((g) => g.status === 'LIVE');
    const timer = setInterval(load, live ? 30000 : 300000);
    return () => clearInterval(timer);
  }, [data, load]);

  const games = (data?.games || []).filter((g) => filter === 'all' || g.team_key === filter);
  const today = etDay(new Date().toISOString());
  const now = new Date();

  const live = games.filter((g) => g.status === 'LIVE');
  const todayGames = games
    .filter((g) => g.status !== 'LIVE' && etDay(g.date) === today)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const upcoming = games
    .filter((g) => g.status !== 'LIVE' && etDay(g.date) !== today && new Date(g.date) > now && g.status !== 'FINAL')
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const recent = games
    .filter((g) => g.status === 'FINAL' && etDay(g.date) !== today)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const empty = live.length + todayGames.length + upcoming.length + recent.length === 0;

  return (
    <div data-testid="scores-page" className="min-h-screen bg-slate-50">
      <div className="bg-[#002868] border-b-2 border-[#BF0A30]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFB612]">Washington, D.C.</p>
          <h1 className="font-serif-head font-extrabold text-3xl sm:text-4xl text-white tracking-tight mt-2">
            Scores & Schedule
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`scores-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 text-xs font-heading font-medium uppercase tracking-wide border transition-colors shrink-0 ${
                filter === f.key
                  ? 'bg-[#002868] text-white border-[#002868]'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-[#002868]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {!data ? (
          <p className="text-sm text-slate-500 py-12 text-center">Loading scores…</p>
        ) : data.available === false && empty ? (
          <p data-testid="scores-unavailable" className="text-sm text-slate-500 py-12 text-center">
            Scores temporarily unavailable. Please check back shortly.
          </p>
        ) : (
          <>
            {data.stale && data.updated_at && (
              <p className="text-xs text-slate-400 mb-6">
                Showing last available scores · updated{' '}
                {new Date(data.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
            <Section title="Live Now" accent="#BF0A30" games={live} testid="scores-live-section" />
            <Section title="Today" accent="#002868" games={todayGames} testid="scores-today-section" />
            <Section title="Upcoming" accent="#002868" games={upcoming} testid="scores-upcoming-section" />
            <Section title="Recent Results" accent="#64748B" games={recent} testid="scores-recent-section" />
            {empty && (
              <p className="text-sm text-slate-500 py-12 text-center">No games found for this selection.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
